import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Undo2, Lock, LockOpen } from 'lucide-react';
import { api } from '../../lib/api';
import Modal from '../ui/Modal';
import { Money, Empty, fmtDate, usd, parseCents, today, inputCls, btnCls, btn2Cls, cardCls, thCls, tdCls } from './shared';

// Paybacks (LP-309 slice B): what CCS owes partners and partner companies, from the ledger's
// payback accounts (2100, 2110, 2120 Reimbursements Payable; 2130 Due to Applied Flooring).
// Partner-paid costs and their repayments post from Partner Investments; Applied Flooring is
// repaid here. While anything is owed, no member distribution records, and no fee payout
// records unless the paybacks_block_fee_payouts setting is false.

export const enabled = true;

// A fresh key per form, so a retried submit returns the same entry instead of posting twice.
function newKey() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const b = globalThis.crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

const SOURCE_LABEL = { opening: 'Opening balance', investment: 'Partner Investments', payback: 'Repayment', manual: 'Manual entry', relay_csv: 'Bank import', qbo_gl: 'QuickBooks history' };

export default function PaybacksTab({ me, reloadShared, onGo }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [creditor, setCreditor] = useState('');
  const [repaying, setRepaying] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.get('/books/paybacks')); setError(''); } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => (data ? data.history.filter((h) => !creditor || h.code === creditor).slice().reverse() : []), [data, creditor]);

  if (error && !data) return <p className="text-sm text-rose-600">{error}</p>;
  if (!data) return <p className="text-sm text-[var(--text-secondary)]">Loading paybacks...</p>;
  const applied = data.creditors.find((c) => c.code === data.repayment_code);
  const byCode = Object.fromEntries(data.creditors.map((c) => [c.code, c]));

  return (
    <div className="space-y-5">
      {error && <p className="text-sm text-rose-600">{error}</p>}

      <section className={`${cardCls} ${data.total_cents > 0 ? 'border-amber-300 dark:border-amber-700' : ''}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs text-[var(--text-secondary)]">CCS owes, as of {fmtDate(data.as_of)}</div>
            <div className="text-2xl font-semibold text-[var(--text-primary)]"><Money cents={data.total_cents} /></div>
          </div>
          <ul className="space-y-1 text-sm">
            <li className="flex items-center gap-1.5">
              {data.fee_payouts_blocked ? <Lock size={14} className="text-amber-600" /> : <LockOpen size={14} className="text-emerald-600" />}
              Fee payouts {data.fee_payouts_blocked ? 'are blocked until the paybacks are repaid' : data.block_fee_payouts ? 'can be recorded (nothing is owed)' : 'can be recorded (the setting lets fees through while paybacks are open)'}
            </li>
            <li className="flex items-center gap-1.5">
              {data.distributions_blocked ? <Lock size={14} className="text-amber-600" /> : <LockOpen size={14} className="text-emerald-600" />}
              Member distributions {data.distributions_blocked ? 'are blocked until the paybacks are repaid' : 'can be recorded'}
            </li>
          </ul>
        </div>
        {data.later_cents !== 0 && <p className="mt-2 text-xs text-[var(--text-secondary)]">Entries dated after today change this by {usd(data.later_cents)} when their date comes.</p>}
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-semibold text-[var(--text-primary)]">Owed to</h2>
          {me.can_edit && applied && applied.owed_cents > 0 && <button className={btnCls} onClick={() => setRepaying(true)}><Undo2 size={14} /> Record repayment to {applied.creditor}</button>}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {data.creditors.map((c) => (
            <button key={c.code} onClick={() => setCreditor(creditor === c.code ? '' : c.code)}
              className={`${cardCls} text-left ${creditor === c.code ? 'border-primary ring-1 ring-primary' : ''}`}>
              <div className="font-semibold text-[var(--text-primary)]">{c.creditor}</div>
              <div className="text-xs text-[var(--text-secondary)]">{c.code} {c.name}</div>
              <div className={`mt-2 text-lg font-semibold ${c.owed_cents > 0 ? 'text-amber-700 dark:text-amber-400' : ''}`}><Money cents={c.owed_cents} /></div>
              {c.balance_cents < 0 && <div className="text-xs text-rose-600">Repaid {usd(-c.balance_cents)} more than was owed</div>}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-[var(--text-secondary)]">
          Partners are repaid from <Link className="underline" to="/investments">Partner Investments</Link> ("Mark reimbursed"), which posts the repayment here.
          {onGo && <> The fee payouts themselves are on <button className="underline" onClick={() => onGo('payouts')}>Deal payouts</button>.</>}
        </p>
      </section>

      {data.investments && (
        <section>
          <h2 className="mb-2 font-semibold text-[var(--text-primary)]">Ledger and Partner Investments</h2>
          <div className="overflow-x-auto rounded-xl border border-[var(--border)] bg-white dark:bg-slate-900">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-gray-50 dark:bg-slate-800"><tr>
                <th className={thCls}>Partner</th><th className={`${thCls} text-right`}>Ledger</th><th className={`${thCls} text-right`}>Investments rows, still owed</th>
                <th className={`${thCls} text-right`}>Difference</th><th className={thCls}>Covered by the opening balance</th>
              </tr></thead>
              <tbody>
                {data.investments.map((m) => (
                  <tr key={m.member} className="border-t border-[var(--border)]">
                    <td className={tdCls}>{m.member}</td>
                    <td className={`${tdCls} text-right`}><Money cents={m.ledger_cents} /></td>
                    <td className={`${tdCls} text-right`}><Money cents={m.investments_owed_cents} /></td>
                    <td className={`${tdCls} text-right ${m.difference_cents ? 'font-semibold text-amber-700 dark:text-amber-400' : ''}`}><Money cents={m.difference_cents} /></td>
                    <td className={`${tdCls} text-xs`}>
                      {m.covered_count} {m.covered_count === 1 ? 'row' : 'rows'}, {usd(m.covered_cents)}{m.covered_open_cents !== m.covered_cents && ` (${usd(m.covered_open_cents)} still open)`}
                      {/* PENDING KEVIN (LP-350 Q6): these rows wait for the accountant's per-row answer (covered or settled outside). */}
                      {m.uncovered_before_cutover_count > 0 && <div className="text-amber-700 dark:text-amber-400">{m.uncovered_before_cutover_count} open {m.uncovered_before_cutover_count === 1 ? 'row' : 'rows'} on or before the cutover not covered ({usd(m.uncovered_before_cutover_cents)}): Books refuses their repayment until they are ticked covered (if the opening balance includes them){m.member !== 'Chad' && ', or marked settled outside the books, once the accountant answers for each row'}.</div>}
                      {m.settled_outside_count > 0 && <div className="text-[var(--text-secondary)]">{m.settled_outside_count} settled outside the books before the cutover ({usd(m.settled_outside_cents)}), not owed.</div>}
                      {m.unposted_count > 0 && <div className="text-amber-700 dark:text-amber-400">{m.unposted_count} {m.unposted_count === 1 ? 'row' : 'rows'} after the cutover not in Books yet ({usd(m.unposted_cents)}): the lead posts them at go-live (books_post_investment){m.unposted_repaid_count > 0 && `, except ${m.unposted_repaid_count} repaid meanwhile, which are posted by hand`}.</div>}
                      {m.disputed_count > 0 && <div className="text-[var(--text-secondary)]">{m.disputed_count} needing confirmation, not counted.</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-1 text-xs text-[var(--text-secondary)]">
            A covered row is inside the 1/1/2026 opening balance: it never posts again, and its repayment posts against that balance.
            {/* PENDING KEVIN (LP-350 Q2): books_covered_through() sets this date (the cutover until the accountant answers). */}
            {data.covered_through ? ` Only rows dated on or before ${fmtDate(data.covered_through)} can be covered; later rows post from Partner Investments.` : ''}
          </p>
        </section>
      )}

      <section>
        <div className="mb-2 flex items-center gap-2">
          <h2 className="font-semibold text-[var(--text-primary)]">History</h2>
          <select className={inputCls} value={creditor} onChange={(e) => setCreditor(e.target.value)} aria-label="Creditor">
            <option value="">Everyone</option>
            {data.creditors.map((c) => <option key={c.code} value={c.code}>{c.creditor}</option>)}
          </select>
        </div>
        {rows.length === 0 ? <Empty>Nothing posted to the payback accounts yet. The opening balances at 1/1/2026 are posted by the lead at go-live.</Empty> : (
          <div className="overflow-x-auto rounded-xl border border-[var(--border)] bg-white dark:bg-slate-900">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-gray-50 dark:bg-slate-800"><tr>
                <th className={thCls}>Date</th><th className={thCls}>Owed to</th><th className={thCls}>What</th>
                <th className={`${thCls} text-right`}>Owed</th><th className={`${thCls} text-right`}>Repaid</th><th className={`${thCls} text-right`}>Balance</th>
              </tr></thead>
              <tbody>
                {rows.map((h) => (
                  <tr key={h.line_id} className="border-t border-[var(--border)]">
                    <td className={`${tdCls} whitespace-nowrap`}>{fmtDate(h.entry_date)}</td>
                    <td className={tdCls}>{byCode[h.code]?.creditor || h.code}</td>
                    <td className={tdCls}>
                      <div>{h.memo}</div>
                      <div className="text-xs text-[var(--text-secondary)]">{SOURCE_LABEL[h.source] || h.source}{h.source_ref ? ` · ${h.source_ref}` : ''}</div>
                    </td>
                    <td className={`${tdCls} text-right`}>{h.owed_added_cents ? <Money cents={h.owed_added_cents} /> : ''}</td>
                    <td className={`${tdCls} text-right text-emerald-700 dark:text-emerald-400`}>{h.repaid_cents ? <Money cents={h.repaid_cents} /> : ''}</td>
                    <td className={`${tdCls} text-right`}><Money cents={h.balance_cents} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {repaying && applied && <RepayForm creditor={applied} onClose={() => setRepaying(false)} onSaved={() => { setRepaying(false); load(); reloadShared(); }} />}
    </div>
  );
}

function RepayForm({ creditor, onClose, onSaved }) {
  const [key] = useState(newKey);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(today());
  const [memo, setMemo] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  async function save(ev) {
    ev.preventDefault();
    const c = parseCents(amount);
    if (!c) { setErr('Enter the amount repaid in dollars, e.g. 1,000.00'); return; }
    if (c > creditor.owed_cents) { setErr(`That is more than CCS owes ${creditor.creditor} (${usd(creditor.owed_cents)}).`); return; }
    setBusy(true);
    try {
      await api.post('/books/paybacks/repayments', { id: key, amount_cents: c, entry_date: date, memo: memo || undefined });
      onSaved();
    } catch (e) { setErr(e.message); setBusy(false); }
  }
  return (
    <Modal title={`Record repayment to ${creditor.creditor}`} onClose={onClose} maxWidth="max-w-md">
      <p className="mb-3 text-sm text-[var(--text-secondary)]">Owed now: {usd(creditor.owed_cents)}. Posts Dr {creditor.code} / Cr the bank account. Use the date the money left the bank.</p>
      <form onSubmit={save} className="space-y-3">
        <label className="block text-xs font-medium text-[var(--text-secondary)]">Amount ($)
          <input className={`${inputCls} mt-1 w-full`} inputMode="decimal" required value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
        </label>
        <label className="block text-xs font-medium text-[var(--text-secondary)]">Date paid
          <input className={`${inputCls} mt-1 w-full`} type="date" required value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="block text-xs font-medium text-[var(--text-secondary)]">Memo (wire or check reference)
          <input className={`${inputCls} mt-1 w-full`} value={memo} maxLength={500} onChange={(e) => setMemo(e.target.value)} />
        </label>
        {err && <p className="text-sm text-rose-600">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={btn2Cls} onClick={onClose}>Cancel</button>
          <button className={btnCls} disabled={busy}>{busy ? 'Saving...' : 'Record repayment'}</button>
        </div>
      </form>
    </Modal>
  );
}
